import { cn } from "@/lib/utils";
import React, { useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { LoginForm } from "./login-form";

import { useAuth } from "@/lib/hooks/use-auth";
import { useMutate, useSuspenseFetchAll } from "@/lib/hooks/use-fetch";

import myKeyIcon from "@/assets/images/key-app-icon.png";
import kohostLogo from "@/assets/images/kohost-logo.png";
import { useOrganization } from "@/lib/hooks/use-organization";
import { authQuery, selfQuery } from "@/lib/queries/auth";
import { organizationQuery } from "@/lib/queries/organization";
import { captureException } from "@/services/sentry";
import { ERROR_CODES } from "./error-codes";

const redirectQueryKey = "redirect_uri";
const intentQueryKey = "intent";
const tokenQueryKey = "token";
const orgIdQuery = "organization_id";
const errorQueryKey = "error_code";

export const Login = () => {
  const [searchParams, setSearchParams] = useSearchParams(
    new URLSearchParams(window.location.search),
  );

  const orgIdInvalid =
    searchParams.has(errorQueryKey) &&
    searchParams.get(errorQueryKey) === "ORGANIZATION_NOT_FOUND";

  const queries: any[] = [authQuery(), selfQuery()];

  if (!orgIdInvalid) {
    queries.push(organizationQuery(searchParams.get(orgIdQuery)));
  }

  useSuspenseFetchAll({
    queries: queries,
  });

  const initialErrorMsg = searchParams.get(errorQueryKey)
    ? ERROR_CODES[searchParams.get(errorQueryKey) as keyof typeof ERROR_CODES]
    : "";

  const { startLogin, finishLogin, getToken, currentUser } = useAuth();

  if (initialErrorMsg && searchParams.has(tokenQueryKey)) {
    setSearchParams((params) => {
      params.delete(tokenQueryKey);
      return params;
    });
  }

  const intent = searchParams.get(intentQueryKey);
  const redirectUri = new URL(
    searchParams.get(redirectQueryKey) || window.location.origin,
  ).origin;

  const [errorMessage, setErrorMessage] = React.useState(initialErrorMsg);
  const [isRedirecting, setIsRedirecting] = React.useState(false);

  const { organization } = useOrganization();
  const organizationId = organization?.id;

  const container = useRef(null);

  const appManifest = organization?.appManifest;

  const logoSrc = appManifest?.logo?.src
    ? appManifest?.logo?.src
    : intent === "digitalKey"
      ? myKeyIcon
      : kohostLogo;

  const { mutateAsync: handleGetToken } = getToken;

  const redirectMutation = useMutate({
    mutationFn: async ({ redirectUri }: { redirectUri: string }) => {
      window.open(redirectUri, "_self");
    },
    onMutate: () => {
      setIsRedirecting(true);
    },
    onError: () => {
      setIsRedirecting(false);
    },
  });

  const myKeyRedirectMutation = useMutate({
    mutationFn: async ({ organizationId }: { organizationId: string }) => {
      console.log("getting login token in render", organizationId);
      const { token } = await handleGetToken({});
      const redirectUri = `mykey://?token=${token}&organizationId=${organizationId}`;
      return redirectUri;
    },
    onError: (error) => {
      captureException(error);
      setErrorMessage(ERROR_CODES.SESSION_EXPIRED);
      setSearchParams((params) => {
        params.delete(tokenQueryKey);
        return params;
      });
      setIsRedirecting(false);
    },
    onSuccess: (redirectUri) => {
      redirectMutation.mutate({ redirectUri });
    },
    onMutate: () => {
      setIsRedirecting(true);
    },
  });

  return (
    <>
      <div
        className={cn(
          "relative h-screen overflow-x-hidden transition-opacity duration-100 h-screen-ios",
        )}
      >
        <div
          ref={container}
          className="relative mx-8 flex h-screen max-w-2xl flex-col justify-center transition-all h-screen-ios md:mx-auto"
        >
          <div className="mb-4 text-left">
            <img
              src={logoSrc}
              alt="Logo"
              className="relative max-h-[75px] max-w-[200px]"
            />
          </div>

          <LoginForm
            currentUser={currentUser ?? null}
            onLoginSuccess={() => {
              const isDigitalKeyLogin = intent === "digitalKey";
              if (isDigitalKeyLogin) {
                myKeyRedirectMutation.mutate({ organizationId });
              } else {
                redirectMutation.mutate({ redirectUri });
              }
            }}
            startLogin={startLogin}
            finishLogin={finishLogin}
            isRedirecting={isRedirecting}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
          />
        </div>
      </div>
    </>
  );
};
